import { getSuggestedLayout } from '@components/Card/CardHeader/utilities';
import {
  generateFieldID,
  getDefaultLabel,
  getDefaultUrlValue,
  getFieldAttributes,
  insertFieldOfTypeInCardFieldOrder,
} from '@modules/card-helpers';
import {
  arrayMove,
  createOrAddToArray,
  modifyArrayAtId,
  modifyArrayAtIndex,
  removeArrayValueAtId,
  removeArrayValueAtIndex,
  toggleArrayValueAtId,
} from '@modules/helpers';
import type {
  CardAddress,
  CardEmail,
  CardFields,
  CardImage,
  CardLayout,
  CardMetadata,
  CardPhoneNumber,
  CardUrl,
} from 'types';

interface InputsFromProfile {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  profileImageUrl: string;
  wasAnyProfilePreviouslySelected: boolean;
}

export type CardFieldAction =
  | { type: 'ADD_NAME' }
  | { type: 'UPDATE_FIRST_NAME'; value: string }
  | { type: 'UPDATE_MIDDLE_NAME'; value: string }
  | { type: 'UPDATE_LAST_NAME'; value: string }
  | { type: 'UPDATE_PREFIX'; value: string }
  | { type: 'UPDATE_SUFFIX'; value: string }
  | { type: 'UPDATE_MAIDEN_NAME'; value: string }
  | { type: 'UPDATE_PREFERRED_NAME'; value: string }
  | { type: 'UPDATE_PRONOUNS'; value: string }
  | { type: 'REMOVE_NAME' }
  | { type: 'ADD_COMPANY'; value?: string }
  | { type: 'UPDATE_COMPANY'; value: string }
  | { type: 'REMOVE_COMPANY' }
  | { type: 'ADD_JOB_TITLE' }
  | { type: 'UPDATE_JOB_TITLE'; value: string }
  | { type: 'REMOVE_JOB_TITLE' }
  | { type: 'ADD_DEPARTMENT' }
  | { type: 'UPDATE_DEPARTMENT'; value: string }
  | { type: 'REMOVE_DEPARTMENT' }
  | { type: 'ADD_ACCREDITATION' }
  | { type: 'UPDATE_ACCREDITATION'; value: string; index: number }
  | { type: 'UPDATE_ALL_ACCREDITATIONS'; value: string[] }
  | { type: 'REMOVE_ACCREDITATION'; index: number }
  | { type: 'REMOVE_ALL_ACCREDITATIONS' }
  | { type: 'ADD_HEADLINE' }
  | { type: 'UPDATE_HEADLINE'; value: string }
  | { type: 'REMOVE_HEADLINE' }
  | { type: 'ADD_PHONE_NUMBER'; id?: string }
  | { type: 'UPDATE_PHONE_NUMBER'; value: CardPhoneNumber; id: string }
  | { type: 'REMOVE_PHONE_NUMBER'; id: string }
  | { type: 'ADD_EMAIL'; id?: string; value?: string }
  | { type: 'UPDATE_EMAIL'; value: CardEmail; id: string }
  | { type: 'REMOVE_EMAIL'; id: string }
  | { type: 'ADD_ADDRESS'; id?: string }
  | { type: 'UPDATE_ADDRESS'; value: CardAddress; id: string }
  | { type: 'REMOVE_ADDRESS'; id: string }
  | { type: 'ADD_URL'; urlType: CardUrl['type']; id?: string }
  | { type: 'UPDATE_URL'; value: CardUrl; id: string }
  | { type: 'REMOVE_URL'; id: string }
  | { type: 'SET_LAYOUT'; value: CardLayout }
  | { type: 'SET_PROFILE_PICTURE'; value: CardImage; layout: CardLayout | undefined }
  | { type: 'REMOVE_PROFILE_PICTURE'; layout: CardLayout | undefined }
  | { type: 'SET_COMPANY_LOGO'; value: CardImage; layout: CardLayout | undefined }
  | { type: 'REMOVE_COMPANY_LOGO'; layout: CardLayout | undefined }
  | { type: 'SET_COVER_PHOTO'; value: CardImage; layout: CardLayout | undefined }
  | { type: 'REMOVE_COVER_PHOTO'; layout: CardLayout | undefined }
  | { type: 'UNLINK_ATTRIBUTE'; id: string }
  | { type: 'UNLINK_ATTRIBUTES'; ids: string[] }
  | { type: 'REORDER_FIELDS'; value: { from: number; to: number } }
  | { type: 'UPDATE_ALL'; value: CardFields }
  | { type: 'UPDATE_INPUTS_FROM_PROFILE'; value: InputsFromProfile };

// We currently don't use any identifiers on things like accreditations, URLs, ect (i.e. fields that
// can have more than one value). So we can use either the index or the values to find the item to either
// update or remove. I've opted for the index because we display the data in the same order that it is represented
// in the data (since we will allow for users to update the order). Using the index works as long as this assumption
// holds true. In the future it will be better to be explicit and use IDs and add ordering but for now that is too much
// work.
export function cardFieldsReducer(state: CardFields, action: CardFieldAction): CardFields {
  switch (action.type) {
    case 'ADD_NAME':
      return { ...state, firstName: '' };
    case 'UPDATE_FIRST_NAME':
      return { ...state, firstName: action.value };
    case 'UPDATE_MIDDLE_NAME':
      return { ...state, middleName: action.value };
    case 'UPDATE_LAST_NAME':
      return { ...state, lastName: action.value };
    case 'UPDATE_PREFIX':
      return { ...state, honorificPrefix: action.value };
    case 'UPDATE_SUFFIX':
      return { ...state, honorificSuffix: action.value };
    case 'UPDATE_MAIDEN_NAME':
      return { ...state, maidenName: action.value };
    case 'UPDATE_PREFERRED_NAME':
      return { ...state, preferredName: action.value };
    case 'UPDATE_PRONOUNS':
      return { ...state, pronouns: action.value };
    case 'REMOVE_NAME':
      return {
        ...state,
        firstName: '',
        middleName: '',
        lastName: '',
        honorificPrefix: '',
        honorificSuffix: '',
        maidenName: '',
        preferredName: '',
        pronouns: '',
        linkedAttributes: state.linkedAttributes?.filter(
          (attribute) => !getFieldAttributes('name').includes(attribute),
        ),
      };
    case 'ADD_COMPANY':
      return { ...state, orgName: action.value ?? '' };
    case 'UPDATE_COMPANY':
      return { ...state, orgName: action.value };
    case 'REMOVE_COMPANY':
      return {
        ...state,
        orgName: '',
        linkedAttributes: state.linkedAttributes?.filter((attribute) => attribute !== 'org_name'),
      };
    case 'ADD_JOB_TITLE':
      return { ...state, jobTitle: '' };
    case 'UPDATE_JOB_TITLE':
      return { ...state, jobTitle: action.value };
    case 'REMOVE_JOB_TITLE':
      return {
        ...state,
        jobTitle: '',
        linkedAttributes: state.linkedAttributes?.filter((attribute) => attribute !== 'job_title'),
      };
    case 'ADD_DEPARTMENT':
      return { ...state, department: '' };
    case 'UPDATE_DEPARTMENT':
      return { ...state, department: action.value };
    case 'REMOVE_DEPARTMENT':
      return {
        ...state,
        department: '',
        linkedAttributes: state.linkedAttributes?.filter((attribute) => attribute !== 'department'),
      };
    case 'ADD_ACCREDITATION':
      return {
        ...state,
        accreditations: createOrAddToArray(
          state.accreditations?.filter((v) => v),
          '',
        ),
      };
    case 'UPDATE_ACCREDITATION':
      return {
        ...state,
        accreditations: modifyArrayAtIndex(state.accreditations, action.index, action.value),
      };
    case 'UPDATE_ALL_ACCREDITATIONS':
      return {
        ...state,
        accreditations: action.value,
      };
    case 'REMOVE_ACCREDITATION':
      return {
        ...state,
        accreditations: removeArrayValueAtIndex(state.accreditations, action.index),
      };
    case 'REMOVE_ALL_ACCREDITATIONS':
      return { ...state, accreditations: [] };
    case 'ADD_HEADLINE':
      return { ...state, headline: '' };
    case 'UPDATE_HEADLINE':
      return { ...state, headline: action.value };
    case 'REMOVE_HEADLINE':
      return { ...state, headline: '' };
    case 'ADD_PHONE_NUMBER': {
      const id = action.id ?? generateFieldID();

      return {
        ...state,
        phoneNumbers: createOrAddToArray(state.phoneNumbers, {
          label: getDefaultLabel('phone'),
          value: '',
          id: id,
          isPreset: false,
        }),
        fieldOrder: insertFieldOfTypeInCardFieldOrder(state, id),
      };
    }
    case 'UPDATE_PHONE_NUMBER':
      return {
        ...state,
        phoneNumbers: modifyArrayAtId(state.phoneNumbers, action.id, action.value),
      };
    case 'REMOVE_PHONE_NUMBER':
      return {
        ...state,
        phoneNumbers: removeArrayValueAtId(state.phoneNumbers, action.id),
        linkedAttributes: state.linkedAttributes?.filter((attribute) => attribute !== action.id),
        fieldOrder: state.fieldOrder.filter((id) => id !== action.id),
      };
    case 'ADD_EMAIL': {
      const id = action.id ?? generateFieldID();

      return {
        ...state,
        emails: createOrAddToArray(state.emails, {
          label: '',
          value: action.value ?? '',
          id: id,
          isPreset: false,
        }),
        fieldOrder: insertFieldOfTypeInCardFieldOrder(state, id),
      };
    }
    case 'UPDATE_EMAIL':
      return {
        ...state,
        emails: modifyArrayAtId(state.emails, action.id, action.value),
      };
    case 'REMOVE_EMAIL':
      return {
        ...state,
        emails: removeArrayValueAtId(state.emails, action.id),
        linkedAttributes: state.linkedAttributes?.filter((attribute) => attribute !== action.id),
        fieldOrder: state.fieldOrder.filter((id) => id !== action.id),
      };
    case 'ADD_ADDRESS': {
      const id = action.id ?? generateFieldID();

      return {
        ...state,
        addresses: createOrAddToArray(state.addresses, {
          label: getDefaultLabel('address'),
          value: '',
          type: 'org',
          id: id,
          isPreset: false,
        }),
        fieldOrder: insertFieldOfTypeInCardFieldOrder(state, id),
      };
    }
    case 'UPDATE_ADDRESS':
      return {
        ...state,
        addresses: modifyArrayAtId(state.addresses, action.id, action.value),
      };
    case 'REMOVE_ADDRESS':
      return {
        ...state,
        addresses: removeArrayValueAtId(state.addresses, action.id),
        linkedAttributes: state.linkedAttributes?.filter((attribute) => attribute !== action.id),
        fieldOrder: state.fieldOrder.filter((id) => id !== action.id),
      };
    case 'ADD_URL': {
      const id = action.id ?? generateFieldID();

      return {
        ...state,
        urls: createOrAddToArray(state.urls, {
          title: '',
          value: '',
          type: action.urlType,
          id: id,
          isPreset: false,
          userValue: getDefaultUrlValue(action.urlType),
        }),
        fieldOrder: insertFieldOfTypeInCardFieldOrder(state, id),
      };
    }
    case 'UPDATE_URL':
      return {
        ...state,
        urls: modifyArrayAtId(state.urls, action.id, action.value),
      };
    case 'REMOVE_URL':
      return {
        ...state,
        urls: removeArrayValueAtId(state.urls, action.id),
        linkedAttributes: state.linkedAttributes?.filter((attribute) => attribute !== action.id),
        fieldOrder: state.fieldOrder.filter((id) => id !== action.id),
      };
    case 'SET_LAYOUT':
      return {
        ...state,
        layout: action.value,
      };
    case 'SET_PROFILE_PICTURE':
      return {
        ...state,
        profilePictureImg: action.value,
        layout: action.layout,
      };
    case 'REMOVE_PROFILE_PICTURE':
      // We don't unlink here because the profile picture can be removed before it's saved (when you change
      // the picture). We don't want the link symbol to dissapear immediately, only on save/delete.
      return {
        ...state,
        profilePictureImg: null,
        layout: action.layout,
      };
    case 'SET_COMPANY_LOGO':
      return {
        ...state,
        companyLogoImg: action.value,
        layout: action.layout,
      };
    case 'REMOVE_COMPANY_LOGO':
      return { ...state, companyLogoImg: null, layout: action.layout };
    case 'SET_COVER_PHOTO':
      return {
        ...state,
        coverPhotoImg: action.value,
        layout: action.layout,
      };
    case 'REMOVE_COVER_PHOTO':
      return { ...state, coverPhotoImg: null, layout: action.layout };
    case 'UNLINK_ATTRIBUTE':
      return {
        ...state,
        linkedAttributes: state.linkedAttributes?.filter((id) => id !== action.id),
      };
    case 'UNLINK_ATTRIBUTES':
      return {
        ...state,
        linkedAttributes: state.linkedAttributes?.filter((id) => !action.ids.includes(id)),
      };
    case 'REORDER_FIELDS':
      return {
        ...state,
        fieldOrder: arrayMove(state.fieldOrder ?? [], action.value.from, action.value.to),
      };
    case 'UPDATE_ALL':
      return { ...action.value };
    case 'UPDATE_INPUTS_FROM_PROFILE': {
      const presetEmails = state.emails?.filter((email) => email.isPreset) ?? [];
      const newEmailId = generateFieldID();
      const newEmails = action.value.email
        ? [...presetEmails, { id: newEmailId, label: '', isPreset: false, value: action.value.email }]
        : [...presetEmails];

      const newPhoneNumbers = action.value.wasAnyProfilePreviouslySelected
        ? state.phoneNumbers?.filter((phoneNum) => phoneNum.isPreset)
        : state.phoneNumbers;
      const newUrls = action.value.wasAnyProfilePreviouslySelected
        ? state.urls?.filter((url) => url.isPreset)
        : state.urls;
      const newAddresses = action.value.wasAnyProfilePreviouslySelected
        ? state.addresses?.filter((address) => address.isPreset)
        : state.addresses;

      const newFieldIds = [
        ...newEmails.map((e) => e.id),
        ...(newPhoneNumbers?.map((p) => p.id) ?? []),
        ...(newAddresses?.map((a) => a.id) ?? []),
        ...(newUrls?.map((u) => u.id) ?? []),
      ];
      const newFieldOrder = [
        newEmailId,
        ...state.fieldOrder.filter((fieldId) => newFieldIds.includes(fieldId)),
      ];

      const newProfilePictureImg = state.profilePictureImg?.editedImageUrl
        ? state.profilePictureImg
        : {
            originalImageUrl: action.value.profileImageUrl,
            editedImageUrl: action.value.profileImageUrl,
          };
      const newLayout = getSuggestedLayout({
        logo: state.companyLogoImg?.editedImageUrl,
        cover: state.coverPhotoImg?.editedImageUrl,
        profile: action.value.profileImageUrl,
      });

      return {
        ...state,

        firstName: action.value.firstName,
        lastName: action.value.lastName,
        middleName: action.value.wasAnyProfilePreviouslySelected ? '' : state.middleName,
        honorificPrefix: action.value.wasAnyProfilePreviouslySelected ? '' : state.honorificPrefix,
        honorificSuffix: action.value.wasAnyProfilePreviouslySelected ? '' : state.honorificSuffix,
        maidenName: action.value.wasAnyProfilePreviouslySelected ? '' : state.maidenName,
        pronouns: action.value.wasAnyProfilePreviouslySelected ? '' : state.pronouns,

        jobTitle: action.value.jobTitle,
        emails: newEmails,
        phoneNumbers: newPhoneNumbers,
        urls: newUrls,
        addresses: newAddresses,
        profilePictureImg: newProfilePictureImg,
        layout: newLayout,
        fieldOrder: newFieldOrder,
      };
    }
  }
}

// Not currently in use but probably something to start thinking about
export type CardMetadataAction =
  | {
      type: 'TOGGLE_FIELD_LOCK';
      value: 'job_title' | 'org_name' | 'accreditations' | 'headline' | string;
    }
  | { type: 'TOGGLE_FIELD_TYPE_LOCK'; value: keyof CardFields }
  | { type: 'TOGGLE_LOCK_ALL_FIELD_TYPES' }
  | { type: 'TOGGLE_EMAIL_SIG_FIELD_LOCK'; value: keyof CardFields }
  | { type: 'TOGGLE_LOCK_ALL_EMAIL_SIG_FIELDS' }
  | { type: 'TOGGLE_LOCK_COLOR' };

export function cardMetadataReducer(state: CardMetadata, action: CardMetadataAction): CardMetadata {
  switch (action.type) {
    case 'TOGGLE_FIELD_LOCK':
      return {
        ...(state || {}),
        lockedFields: toggleArrayValueAtId(state.lockedFields, action.value),
      };
    case 'TOGGLE_FIELD_TYPE_LOCK':
      return {
        ...state,
        lockedFieldTypes: toggleArrayValueAtId(state.lockedFieldTypes, action.value),
      };
    case 'TOGGLE_LOCK_ALL_FIELD_TYPES':
      return { ...state, allFieldTypesLocked: !state.allFieldTypesLocked };
    case 'TOGGLE_EMAIL_SIG_FIELD_LOCK':
      return {
        ...state,
        lockedEmailSignatureFields: toggleArrayValueAtId(state.lockedEmailSignatureFields, action.value),
      };
    case 'TOGGLE_LOCK_ALL_EMAIL_SIG_FIELDS':
      return {
        ...state,
        allEmailSignatureFieldsLocked: !state.allEmailSignatureFieldsLocked,
      };
    case 'TOGGLE_LOCK_COLOR':
      return { ...state, colorLocked: !state.colorLocked };
  }
}
